"use client";

import { useMemo, useState } from "react";
import {
  ArrowLeft,
  Share2,
  Users,
  Rocket,
  MonitorPlay,
  Zap,
  CircleCheck,
  Building2,
  Eye,
  ChevronLeft,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import DataTable from "@/components/DataTable";

import AnalyticsStatsCard from "@/components/analytics/AnalyticsStatsCard";
import AnalyticsLineChart from "@/components/analytics/AnalyticsLineChart";
import TenantAnalyticsFilters from "@/components/analytics/TenantAnalyticsFilters";
import { useLocation, useNavigate } from "react-router-dom";
import ExperienceUsersListDialog from "@/components/dialogs/tenants/experiences/ExperienceUsersListDialog";
import {
  useGetCampaignsQuery,
  useGetTenantAnalyticsQuery,
  useGetTenantCampaignsQuery,
} from "@/store/slices/apiSlice";
import CircleLoader from "@/components/common/ActionLoader";

const dummyExperiences = [
  {
    id: "exp-001",
    experienceName: "Fire Safety Training",
    campaignName: "New Employee Onboarding",
    sessions: "1,245",
    completionRate: 87,
    participants: 89,
    avgTime: "14 min 57 sec",
  },
  {
    id: "exp-002",
    experienceName: "Equipment Onboarding...",
    campaignName: "New Employee Onboarding",
    sessions: "1,245",
    completionRate: 87,
    participants: 89,
    avgTime: "14 min 57 sec",
  },
  {
    id: "exp-003",
    experienceName: "Emergency Procedure...",
    campaignName: "APAC Restaurant Readiness Pr...",
    sessions: "1,245",
    completionRate: 87,
    participants: 89,
    avgTime: "14 min 57 sec",
  },
  {
    id: "exp-004",
    experienceName: "Workplace Safety Tour",
    campaignName: "EMEA Safety & Compliance Init...",
    sessions: "1,245",
    completionRate: 87,
    participants: 89,
    avgTime: "14 min 57 sec",
  },
  {
    id: "exp-005",
    experienceName: "Hazard Identification",
    campaignName: "New Employee Onboarding",
    sessions: "1,245",
    completionRate: 87,
    participants: 89,
    avgTime: "14 min 57 sec",
  },
];

const TenantAnalytics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCampaign, setSelectedCampaign] = useState("All Campaigns");
  const [selectedDateRange, setSelectedDateRange] = useState("Last 30 days");

  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const pageSize = 10;
  const tenantId = location.state?.tenantId;
  const tenant = location.state?.tenant;
  const { data: campaignsData } = useGetTenantCampaignsQuery(tenantId);
  const selectedCampaignData = campaignsData?.response?.data?.find(
    (item) => item?.campaignName === selectedCampaign,
  );
  const selectedCampaignId = selectedCampaignData?._id || "";

  const { data, isLoading, isFetching } = useGetTenantAnalyticsQuery({
    tenantId,
    campaignId: selectedCampaignId,
    days:
      selectedDateRange === "Last 7 days"
        ? 7
        : selectedDateRange === "Last 30 days"
          ? 30
          : 90,
  });

  const campaignOptions = [
    "All Campaigns",
    ...(campaignsData?.response?.data?.map((item) => item?.campaignName) || []),
  ];

  const analyticsData = data?.response?.data;

  const sessionsChartData =
    analyticsData?.sessionsOverTime?.map((item) => ({
      // date: item._id,
      date: new Date(item.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      }),
      sessions: item.sessions,
      completions: item.completed || 0,
    })) || [];
  const completionRateData =
    analyticsData?.completionRateTrend?.map((item) => ({
      // date: item._id,
      date: new Date(item.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      }),
      rate:
        item.total > 0 ? Math.round((item.completed / item.total) * 100) : 0,
    })) || [];

  const tenantAnalyticsDetails = {
    name: tenant?.name || "Tenant Analytics",
    subtitle: "Detailed performance insights and metrics",
    stats: [
      {
        label: "Total Users",
        value: analyticsData?.counters?.totalUsers || 0,
        icon: Users,
        iconClassName: "text-[#9C6BFF]",
      },
      {
        label: "Total Campaigns",
        value: analyticsData?.counters?.totalCampaigns || 0,
        icon: Rocket,
        iconClassName: "text-[#F4D84A]",
      },
      {
        label: "Total Experiences",
        value: analyticsData?.counters?.totalExperiences || 0,
        icon: MonitorPlay,
        iconClassName: "text-[#67D8FF]",
      },
      {
        label: "Total Sessions",
        value: analyticsData?.counters?.totalSessions || 0,
        icon: Zap,
        iconClassName: "text-[#FF7A00]",
      },
      {
        label: "Avg Completion Rate",
        value: `${analyticsData?.counters?.avgCompletionRate || 0}%`,
        icon: CircleCheck,
        iconClassName: "text-[#00E261]",
      },
    ],
  };

  const tableData = useMemo(() => {
    return (
      analyticsData?.experiencePerformance?.map((item) => ({
        id: item.experienceId,
        experienceName: item.experienceName,
        campaignName: item.campaignName || "-",
        sessions: item.sessions,
        completionRate: item.completionRate,
        participants: item.participants,
        avgTime: item.avgTime,
        status: item.status,
        avgProgress: item.avgProgress,
      })) || []
    );
  }, [analyticsData]);

  const totalPages = Math.ceil(tableData.length / pageSize) || 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleShareAnalytics = () => {
    // console.log("Share analytics for tenant:", tenantId);
  };

  const handleViewParticipants = (row) => {
    // console.log("View participants:", row);
    setSelectedRow(row);
    setOpenUsersDialog(true);
  };

  const columns = [
    {
      header: "Experience Name",
      accessor: "experienceName",
      render: (row) => (
        <div className="flex min-w-[190px] items-center gap-2 text-neutral-100">
          <MonitorPlay className="h-4 w-4 text-[#9C6BFF]" />
          <span>{row?.experienceName || "-"}</span>
        </div>
      ),
    },
    {
      header: "Campaign Name",
      accessor: "campaignName",
      render: (row) => (
        <div className="flex min-w-[220px] items-center gap-2 text-neutral-100">
          <Rocket className="h-4 w-4 text-[#F4D84A]" />
          <span>{row?.campaignName || "-"}</span>
        </div>
      ),
    },
    {
      header: "Sessions",
      accessor: "sessions",
      render: (row) => (
        <div className="text-neutral-100/85">{row?.sessions ?? "-"}</div>
      ),
    },
    {
      header: "Completion Rate",
      accessor: "completionRate",
      render: (row) => {
        const completionRate = Number(row?.completionRate ?? "-");

        return (
          <div className="flex min-w-[160px] items-center gap-4">
            <div className="h-2 w-[80px] overflow-hidden rounded-full bg-neutral-100/15">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#20D3E6] to-[#8A3FFC]"
                style={{ width: `${completionRate}%` }}
              />
            </div>

            <span className="text-neutral-100">{completionRate}%</span>
          </div>
        );
      },
    },
    {
      header: "Participants",
      accessor: "participants",
      render: (row) => (
        <div className="flex min-w-[150px] items-center gap-3">
          <span className="text-neutral-100/85">
            {row?.participants ?? "-"}
          </span>

          <Button
            type="button"
            variant="outline"
            onClick={() => handleViewParticipants(row)}
            className="h-8 border-[#00A9D6] bg-transparent px-3 text-xs font-semibold text-neutral-100 hover:bg-neutral-100/5 hover:text-neutral-100"
          >
            <Eye className="mr-1 h-3 w-3" />
            View Participants
          </Button>
        </div>
      ),
    },
    {
      header: "Avg Time",
      accessor: "avgTime",
      render: (row) => (
        <div className="min-w-[120px] text-neutral-100/85">
          {row?.avgTime ?? "-"}
        </div>
      ),
    },
  ];
  if (isLoading || isFetching) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <CircleLoader loading={isLoading || isFetching} size={64} />
      </div>
    );
  }
  return (
    <div className="min-h-screen  px-6 py-6 text-neutral-100">
      <Button
        type="button"
        variant="neutral"
        onClick={() => navigate(-1)}
        className="mb-7 rounded-md border border-brand-20  text-xs font-semibold text-[#7EDBFF] hover:bg-white/5"
      >
        <ChevronLeft className="h-4 w-4" />
        Back
      </Button>

      <header className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-center">
        <div>
          <div className="mb-3 flex items-center gap-3">
            <Building2 className="h-8 w-8 text-[#9C6BFF]" />

            <h1 className="text-3xl font-bold tracking-tight">
              {tenantAnalyticsDetails.name}
            </h1>
          </div>

          <p className="text-sm font-semibold text-neutral-100/85">
            {tenantAnalyticsDetails.subtitle}
          </p>
        </div>

        <Button
          type="button"
          onClick={handleShareAnalytics}
          disabled
          className="h-9 w-fit bg-[#00D3F2] px-5 text-sm font-bold text-black hover:bg-[#00BBD7]"
        >
          <Share2 className="mr-2 h-4 w-4" />
          Share Analytics
        </Button>
      </header>

      <section className="mb-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {tenantAnalyticsDetails.stats.map((item) => (
          <AnalyticsStatsCard
            key={item.label}
            label={item.label}
            value={item.value}
            icon={item.icon}
            iconClassName={item.iconClassName}
          />
        ))}
      </section>

      <section className="mb-6 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
        <h2 className="text-lg font-bold text-neutral-100">
          Campaign Based Experience Performance Report
        </h2>

        <TenantAnalyticsFilters
          selectedCampaign={selectedCampaign}
          selectedDateRange={selectedDateRange}
          onCampaignChange={setSelectedCampaign}
          onDateRangeChange={setSelectedDateRange}
          campaignOptions={campaignOptions}
        />
      </section>

      <section className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <AnalyticsLineChart
          title="Sessions Over Time"
          data={sessionsChartData}
          lines={[
            {
              dataKey: "sessions",
              name: "Sessions",
              stroke: "#00D3F2",
            },
            {
              dataKey: "completions",
              name: "Completions",
              stroke: "#9C6BFF",
            },
          ]}
          yDomain={[0, 200]}
        />

        <AnalyticsLineChart
          title="Completion Rate Trend"
          data={completionRateData}
          lines={[
            {
              dataKey: "rate",
              name: "Rate",
              stroke: "#00C98B",
            },
          ]}
          yDomain={[0, 100]}
          suffix="%"
        />
      </section>

      <section className="rounded-lg bg-[#111827]/90 p-5 shadow-lg">
        <h2 className="mb-5 text-sm font-bold text-neutral-100">
          Experience Performance
        </h2>
        <div className="max-h-[340px] overflow-auto">
          <DataTable
            data={tableData}
            columns={columns}
            // totalPages={totalPages}
            // currentPage={currentPage}
            // onPageChange={handlePageChange}
            isLoading={isLoading || isFetching}
            searchPlaceholder="Search for..."
            bgColor="[#172D45]"
            tableHeaderBg="[#172D45]"
            borderNone={false}
            showPagination={false}
          />{" "}
        </div>
      </section>

      <ExperienceUsersListDialog
        open={openUsersDialog}
        onClose={() => {
          setOpenUsersDialog(false);
          setSelectedRow(null);
        }}
        experienceDetails={selectedRow}
      />
    </div>
  );
};

export default TenantAnalytics;
